import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, ExternalLink, Eye, Download } from 'lucide-react';
import { saveElementPdf } from '../../utils/savePdf';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { usePASAdminAuth } from '../../context/PASAdminAuthContext';
import { TreasuryCheckFace, TreasuryCheckVoucher } from '../../components/pas/TreasuryCheckPrintParts';
import { BankLookupLog } from '../../components/pas/BankLookupLog';
import { HolderCheckActions } from '../../components/pas/HolderCheckActions';
import { ResendClaimButton } from '../../components/pas/ResendClaimButton';
import { Form1099DA } from '../../components/pas/TreasuryCheckStubs';
import '../../components/check/check-print.css';
import { handleActionError, actionSuccess } from '../../utils/actionFee';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check`;

export default function TreasuryCheckPrint() {
  const navigate = useNavigate();
  const { checkId } = useParams();
  const isAdmin = useLocation().pathname.startsWith('/pas/admin/');
  const clientAuth = usePASClientAuth();
  const adminAuth = usePASAdminAuth();
  const { token, loading } = isAdmin ? adminAuth : clientAuth;
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const savePdf = async () => {
    setSaving(true);
    try { await saveElementPdf(document.querySelector('[data-testid="tc-print-sheet"]'), `Treasury-Check-${c.check_no ?? c.check_id}.pdf`); toast.success('PDF saved'); }
    catch (e) { toast.error('Could not create the PDF'); }
    setSaving(false);
  };
  const base = `${API}/${isAdmin ? 'admin/' : ''}checks/${checkId}`;
  const load = useCallback(() => axios.get(base, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => setC(r.data)).catch(err => setError(err.response?.data?.detail || 'Treasury Check not found')), [base, token]);
  useEffect(() => {
    if (loading) return;
    if (!token) { navigate(isAdmin ? '/pas/admin/login' : '/pas/login'); return; }
    load();
  }, [token, loading, isAdmin, navigate, load]);
  const endorse = async (body) => post('endorse', body);
  const post = async (path, body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${base}/${path}`, body, { headers: { Authorization: `Bearer ${token}` } });
      actionSuccess(r.data); await load();
    } catch (err) { handleActionError(err, token, 'Action failed'); }
    finally { setBusy(false); }
  };
  const closed = ['redeemed', 'deposited'].includes(c?.status);
  if (error) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-red-400" data-testid="tc-print-error">{error}</div>;
  if (!c) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Loading...</div>;
  
  const handlePrintPreview = () => {
    // Open print preview in a new window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups for print preview');
      return;
    }
    
    // Get the check sheet HTML
    const checkSheet = document.querySelector('[data-testid="tc-print-sheet"]');
    if (!checkSheet) return;
    
    // Get all stylesheets
    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Preview - Treasury Check No. ${c.check_no}</title>
        <style>
          ${styles}
          body { margin: 0; padding: 20px; background: #fff; }
          .check-sheet { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
          @media print {
            body { padding: 0; }
            .check-sheet { box-shadow: none; }
          }
          .print-controls { 
            position: fixed; top: 20px; right: 20px; z-index: 1000;
            display: flex; gap: 10px;
          }
          .print-controls button {
            padding: 12px 24px; border: none; border-radius: 8px; cursor: pointer;
            font-weight: 600; font-size: 14px;
          }
          .print-btn { background: #10b981; color: white; }
          .close-btn { background: #64748b; color: white; }
          @media print { .print-controls { display: none; } }
        </style>
      </head>
      <body>
        <div class="print-controls">
          <button class="print-btn" onclick="window.print()">🖨️ Print</button>
          <button class="close-btn" onclick="window.close()">✕ Close</button>
        </div>
        ${checkSheet.outerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
  };
  
  return (
    <div className="min-h-screen bg-slate-900 tc-print-root">
      <header className="bg-slate-800 border-b border-slate-700 print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(isAdmin ? '/pas/admin/dashboard' : '/pas/banking')} className="p-2 text-slate-400 hover:text-white" data-testid="tc-print-back"><ArrowLeft className="w-5 h-5" /></button>
            <div><h1 className="text-lg font-bold text-white">Treasury Check No. {c.check_no ?? '-'}</h1>
              <p className="text-xs text-slate-400">${Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {c.network} · {c.status}</p></div>
          </div>
          <div className="flex gap-3">
            <a href={`/verify-treasury-check/${c.check_id}?t=${c.verify_token}`} target="_blank" rel="noreferrer" data-testid="tc-verify-link" 
              className="px-4 py-2 border border-emerald-600 text-emerald-400 rounded-lg text-sm flex items-center gap-2 hover:bg-emerald-600/10">
              Verify Hashes
            </a>
            <a href={c.explorer_url} target="_blank" rel="noreferrer" data-testid="tc-print-etherscan" className="px-4 py-2 border border-slate-600 text-slate-200 rounded-lg text-sm flex items-center gap-2">On-chain <ExternalLink className="w-4 h-4" /></a>
            <button onClick={handlePrintPreview} data-testid="tc-preview-btn" className="px-4 py-2 border border-slate-600 text-slate-200 rounded-lg text-sm flex items-center gap-2 hover:bg-slate-700">
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button onClick={savePdf} disabled={saving} data-testid="tc-pdf-btn" className="px-4 py-2 border border-slate-600 text-slate-200 rounded-lg text-sm flex items-center gap-2 hover:bg-slate-700 disabled:opacity-50">
              <Download className="w-4 h-4" /> {saving ? 'Saving…' : 'Save PDF'}
            </button>
            <button onClick={() => window.print()} data-testid="tc-print-btn" className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2"><Printer className="w-5 h-5" /> Print</button>
          </div>
        </div>
      </header>
      {!isAdmin && c.can_resend_claim && <div className="max-w-4xl mx-auto px-4 pt-6 print:hidden"><ResendClaimButton check={c} token={token} onDone={load} /></div>}
      {isAdmin && <BankLookupLog checkId={checkId} token={token} />}
      {c.can_endorse && !closed && <div className="max-w-4xl mx-auto px-4 pt-6 print:hidden"><HolderCheckActions check={c} busy={busy} onEndorse={endorse} onProcess={(b) => post('process', b)} onRedeem={(b) => post('redeem', b)} token={token} /></div>}
      {closed && <p className="max-w-4xl mx-auto px-4 pt-6 text-sm text-emerald-300 print:hidden" data-testid="tc-closed-status">This check is {c.status} under order {c.redemption_order?.order_no}.</p>}
      {!c.can_endorse && c.current_holder_party && <p className="max-w-4xl mx-auto px-4 pt-6 text-sm text-slate-400 print:hidden" data-testid="tc-current-holder">Current holder: {c.current_holder_party.name || c.current_holder}</p>}
      <div className="overflow-x-auto py-10 print:p-0">
        <div className="check-sheet" data-testid="tc-print-sheet">
          <TreasuryCheckFace c={c} />
          <div className="irs-gap" data-testid="tc-detach-line"><span>✂ DETACH HERE · KEEP STUB FOR YOUR RECORDS</span></div>
          <TreasuryCheckVoucher c={c} />
          <div className="irs-gap" data-testid="tc-detach-line"><span>✂ DETACH HERE · KEEP STUB FOR YOUR RECORDS</span></div>
          <Form1099DA f={c.form_1099da} />
        </div>
      </div>
    </div>
  );
}
